/*
 * MOTORCYCLE CART GUARD
 * Product ID: 15447607247121
 *
 * Prevents mixing the motorcycle with other products
 * through normal storefront add-to-cart controls.
 */

(() => {
  if (window.MotorcycleCartGuard) return;

  const MOTORCYCLE_ID = '15447607247121';

  const MESSAGE_MOTORCYCLE =
    'The motorcycle must be purchased separately. ' +
    'Please remove all other items from your cart first.';

  const MESSAGE_OTHER =
    'Your cart contains a motorcycle that must be ' +
    'purchased separately. Please remove the motorcycle ' +
    'before adding other products.';

  const MESSAGE_ERROR =
    'We could not check your cart. Please try again.';

  const bypassClicks = new WeakSet();
  const bypassSubmits = new WeakSet();

  let checking = false;

  function getRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  async function getCart() {
    const response = await fetch(`${getRoot()}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Could not load cart');
    }

    return response.json();
  }

  function isMotorcycle(item) {
    return String(item.product_id) === MOTORCYCLE_ID;
  }

  function hasMotorcycle(cart) {
    return cart.items.some(isMotorcycle);
  }

  function hasOtherProducts(cart) {
    return cart.items.some(item => !isMotorcycle(item));
  }

  function getProductId(form, button) {
    const productForm =
      form?.closest('product-form-component') ||
      button?.closest('product-form-component');

    const quickAdd =
      button?.closest('quick-add-component');

    const productCard =
      button?.closest('[data-product-id]');

    return (
      productForm?.dataset.productId ||
      quickAdd?.dataset.productId ||
      productCard?.dataset.productId ||
      form?.dataset.productId ||
      null
    );
  }

  function getVariantId(form) {
    return form?.querySelector('[name="id"]')?.value || null;
  }

  async function getVariantProductId(variantId) {
    if (!variantId) return null;

    const cart = await getCart();

    const existing = cart.items.find(
      item => String(item.variant_id) === String(variantId)
    );

    if (existing) {
      return String(existing.product_id);
    }

    return null;
  }

  function showMessage(message) {
    let dialog = document.getElementById(
      'motorcycle-cart-guard-message'
    );

    if (!dialog) {
      dialog = document.createElement('div');
      dialog.id = 'motorcycle-cart-guard-message';
      dialog.className = 'motorcycle-cart-guard-overlay';

      dialog.innerHTML = `
        <div
          class="motorcycle-cart-guard-dialog"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="motorcycle-cart-guard-title"
          aria-describedby="motorcycle-cart-guard-text"
        >
          <button
            type="button"
            class="motorcycle-cart-guard-close"
            aria-label="Close"
          >×</button>

          <h2 id="motorcycle-cart-guard-title">
            Separate purchase required
          </h2>

          <p id="motorcycle-cart-guard-text"></p>

          <button
            type="button"
            class="motorcycle-cart-guard-ok"
          >
            Got it
          </button>
        </div>
      `;

      document.body.appendChild(dialog);

      dialog.querySelectorAll(
        '.motorcycle-cart-guard-close, .motorcycle-cart-guard-ok'
      ).forEach(button => {
        button.addEventListener('click', () => {
          dialog.remove();
        });
      });

      dialog.addEventListener('click', event => {
        if (event.target === dialog) {
          dialog.remove();
        }
      });
    }

    dialog.querySelector(
      '#motorcycle-cart-guard-text'
    ).textContent = message;

    dialog.querySelector(
      '.motorcycle-cart-guard-ok'
    ).focus();
  }

  async function checkProduct(productId, variantId) {
    const cart = await getCart();

    let resolvedProductId = productId
      ? String(productId)
      : null;

    if (!resolvedProductId) {
      resolvedProductId =
        await getVariantProductId(variantId);
    }

    /*
     * Unknown product IDs cannot be reliably classified.
     * A checkout validation rule must enforce this
     * restriction independently.
     */
    if (!resolvedProductId) {
      return true;
    }

    const addingMotorcycle =
      resolvedProductId === MOTORCYCLE_ID;

    if (addingMotorcycle && hasOtherProducts(cart)) {
      showMessage(MESSAGE_MOTORCYCLE);
      return false;
    }

    if (!addingMotorcycle && hasMotorcycle(cart)) {
      showMessage(MESSAGE_OTHER);
      return false;
    }

    return true;
  }

  function stopEvent(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  /*
   * Handle ordinary product-form submissions.
   */
  document.addEventListener(
    'submit',
    async event => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;

      if (bypassSubmits.has(form)) {
        bypassSubmits.delete(form);
        return;
      }

      const action = form.getAttribute('action') || '';
      const isProductForm =
        form.matches('[data-type="add-to-cart-form"]') ||
        form.closest('product-form-component') ||
        action.includes('/cart/add');

      if (!isProductForm) return;

      stopEvent(event);

      if (checking) return;
      checking = true;

      try {
        const productId = getProductId(form, null);
        const variantId = getVariantId(form);

        const allowed = await checkProduct(
          productId,
          variantId
        );

        if (allowed && form.isConnected) {
          bypassSubmits.add(form);
          form.requestSubmit();
        }
      } catch (error) {
        console.error('Motorcycle cart guard:', error);
        showMessage(MESSAGE_ERROR);
      } finally {
        checking = false;
      }
    },
    true
  );

  /*
   * Handle product buttons before Horizon processes
   * their click events.
   */
  document.addEventListener(
    'click',
    async event => {
      const button = event.target.closest?.(
        'button[type="submit"], ' +
        'button[name="add"], ' +
        '[data-quick-add-button="add"]'
      );

      if (!button) return;

      if (bypassClicks.has(button)) {
        bypassClicks.delete(button);
        return;
      }

      const form = button.closest('form');

      const productId = getProductId(form, button);

      const isAddButton =
        Boolean(button.closest('product-form-component')) ||
        button.matches('[data-quick-add-button="add"]') ||
        Boolean(form?.matches('[data-type="add-to-cart-form"]'));

      if (!isAddButton || !productId) return;

      stopEvent(event);

      if (checking) return;
      checking = true;

      try {
        const allowed = await checkProduct(
          productId,
          getVariantId(form)
        );

        if (allowed && button.isConnected) {
          bypassClicks.add(button);
          button.click();
        }
      } catch (error) {
        console.error('Motorcycle cart guard:', error);
        showMessage(MESSAGE_ERROR);
      } finally {
        checking = false;
      }
    },
    true
  );

  window.MotorcycleCartGuard = {
    motorcycleId: MOTORCYCLE_ID,
    getCart,
    hasMotorcycle,
    showMessage
  };
})();